import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from '@dukanti/shared';
import type { StockWorklistRow } from '@dukanti/shared/admin';
import { Package } from 'lucide-react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { StockStatusBadge } from './stock-status-badge';

export interface StockTableProps {
  readonly rows: readonly StockWorklistRow[];
  readonly returnTo: string;
  readonly onViewProduct: (variantId: string) => void;
}

export function StockThumbnail({ url }: { readonly url: string | null }): React.JSX.Element {
  const [failedUrl, setFailedUrl] = useState<string | null>(null);
  return (
    <span
      aria-hidden="true"
      className="bg-admin-surface-muted flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-md"
    >
      {url && failedUrl !== url ? (
        <img
          src={url}
          alt=""
          className="h-full w-full object-cover"
          onError={() => setFailedUrl(url)}
        />
      ) : (
        <Package size={18} strokeWidth={1.5} className="text-muted-foreground" />
      )}
    </span>
  );
}

export function StockTable({ rows, returnTo, onViewProduct }: StockTableProps): React.JSX.Element {
  const { t, dir, lang } = useTranslation();
  return (
    <div
      dir={dir}
      data-testid="stock-table"
      className="border-border bg-card shadow-admin-card hidden overflow-hidden rounded-xl border md:block"
    >
      <Table className="w-full table-fixed">
        <TableHeader>
          <TableRow>
            <TableHead className="w-[48%] px-4 py-3">{t('admin.stock.columns.product')}</TableHead>
            <TableHead className="w-[16%] px-3 py-3">
              {t('admin.stock.worklist.available')}
            </TableHead>
            <TableHead className="w-[18%] px-3 py-3">{t('admin.stock.columns.status')}</TableHead>
            <TableHead className="w-[18%] px-4 py-3 text-end">
              {t('admin.stock.columns.actions')}
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((row) => (
            <TableRow
              key={row.variantId}
              data-testid="stock-table-row"
              data-variant-id={row.variantId}
              data-product-id={row.productId}
              className="border-border hover:bg-admin-surface-muted/50"
            >
              <TableCell className="px-4 py-4 align-top">
                <div className="flex min-w-0 items-start gap-3">
                  <StockThumbnail url={row.primaryImageUrl} />
                  <div className="min-w-0 space-y-1 [overflow-wrap:anywhere]">
                    <p dir="auto" className="text-foreground font-medium">
                      {row.productName}
                    </p>
                    <p dir="auto" className="text-muted-foreground text-sm">
                      {row.optionSummary || t('admin.stock.worklist.defaultVariant')}
                    </p>
                    {row.barcode ? (
                      <p className="text-muted-foreground text-xs">
                        {t('admin.stock.worklist.barcode')}: <bdi dir="ltr">{row.barcode}</bdi>
                      </p>
                    ) : null}
                    <div className="flex flex-wrap gap-1.5 text-xs">
                      {!row.active ? (
                        <span className="bg-admin-surface-muted text-muted-foreground rounded px-2 py-1">
                          {t('admin.stock.worklist.inactive')}
                        </span>
                      ) : null}
                      {!row.productPublished ? (
                        <span className="bg-admin-surface-muted text-muted-foreground rounded px-2 py-1">
                          {t('admin.stock.worklist.unpublished')}
                        </span>
                      ) : null}
                    </div>
                  </div>
                </div>
              </TableCell>
              <TableCell className="px-3 py-4 align-top">
                <span
                  data-testid="stock-available"
                  className="text-foreground text-lg font-semibold tabular-nums"
                >
                  {new Intl.NumberFormat(lang).format(row.availableUnits)}
                </span>
              </TableCell>
              <TableCell className="px-3 py-4 align-top">
                <StockStatusBadge status={row.status} />
              </TableCell>
              <TableCell className="px-4 py-3 text-end align-top">
                <Link
                  to={`/products/${row.productId}`}
                  state={{ stockReturnTo: returnTo }}
                  onClick={() => onViewProduct(row.variantId)}
                  aria-label={t('admin.stock.worklist.viewProductLabel', {
                    product: row.productName,
                    variant: row.optionSummary || t('admin.stock.worklist.defaultVariant'),
                    // React escapes this attribute; keep the merchant identity literal.
                    interpolation: { escapeValue: false },
                  })}
                  className="border-input text-foreground hover:bg-admin-surface-muted focus-visible:ring-ring inline-flex min-h-11 items-center justify-center rounded-md border px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2"
                >
                  {t('admin.stock.worklist.viewProduct')}
                </Link>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
